import * as chartModelUtil from '@thoughtspot/chart-lib/lib/core/chart-model-utils';
import {
    isCustomChart,
    isCustomOrTsHostedChart,
} from '@thoughtspot/chart-lib/lib/core/chart-type-specification-service';
import { getName } from '@thoughtspot/chart-lib/lib/core/column-name-util';
import {
    getCustomChartFieldColumns,
    getCustomChartLegendColumns,
    getCustomChartRadialColumns,
    getCustomChartXAxisColumns,
    getCustomChartYAxisColumns,
} from '@thoughtspot/chart-lib/lib/core/custom-chart-config-utils';
import { isMeasureNameOrValueColumn } from '@thoughtspot/chart-lib/lib/core/measure-names-and-values';
import { translate } from '@thoughtspot/i18n';
import { isGroupingColumn } from '@thoughtspot/visualization-column-util';
import _ from 'lodash';
import {
    AnswerColumn,
    ChartType,
    ChartViz,
    DisplayMode,
    HeadlineViz,
    TableViz,
} from '/@services/generated/graphql-types';

/**
 * Get Custom chart title
 * @return { string } title of chart
 *
 */

export const getCustomChartTitle = (chartModel: ChartViz) => {
    // Currently we do not know the dimension keys for the chart for x and y axis
    // we need to know this mapping so defaulting value to Untitled for custom charts
    const chartType = chartModelUtil.getChartType(chartModel);
    if (isCustomChart(chartType)) {
        return 'Untitled';
    }

    // Measure names/values are layout fields, not user columns.
    const withoutLayoutColumns = (columns: any[]) =>
        columns?.filter(column => !isMeasureNameOrValueColumn(column.column));

    let customChartXAxisColumns = withoutLayoutColumns(
        getCustomChartXAxisColumns(chartModel),
    );
    const customChartYAxisColumns = withoutLayoutColumns(
        getCustomChartYAxisColumns(chartModel),
    );
    let customChartLegendColumns = withoutLayoutColumns(
        getCustomChartLegendColumns(chartModel),
    );
    const customChartRadialColumns = withoutLayoutColumns(
        getCustomChartRadialColumns(chartModel),
    );
    const customChartFieldColumns = withoutLayoutColumns(
        getCustomChartFieldColumns(chartModel),
    );

    // Pivot rows and columns are both groupings, so both go after "by".
    if (chartType === ChartType.AdvancedPivotTable) {
        customChartXAxisColumns = [
            ...(customChartXAxisColumns ?? []),
            ...(customChartLegendColumns ?? []),
        ];
        customChartLegendColumns = [];
    }

    if (customChartFieldColumns && customChartFieldColumns.length > 0) {
        return customChartFieldColumns
            .map(column => {
                return getName(column.column);
            })
            .join(', ');
    }

    let title = (customChartYAxisColumns ?? [])
        .map(column => {
            return getName(column.column);
        })
        .join(', ');
    if (customChartRadialColumns && customChartRadialColumns.length) {
        title += ' ';
        title += translate('AND');
        title += ' ';
        title += getName(customChartRadialColumns[0].column);
    }

    if (customChartXAxisColumns && customChartXAxisColumns.length) {
        title += ' ';
        title += translate('BY');
        title += ' ';
        title += customChartXAxisColumns
            .map(column => {
                return getName(column.column);
            })
            .join(', ');
    }
    if (customChartLegendColumns && customChartLegendColumns.length) {
        title += ' ';
        title += translate('AND');
        title += ' ';
        title += customChartLegendColumns
            .map(column => {
                return getName(column.column);
            })
            .join(', ');
    }

    // show untitled if title is empty, so that the header height is not affected
    // leading to chart height being affected
    return title || 'Untitled';
};

/**
 * Get chart title
 * @return { string } title of chart
 *
 */

export const getChartAutoTitle = (chartModel: ChartViz) => {
    if (isCustomOrTsHostedChart(chartModelUtil.getChartType(chartModel))) {
        return getCustomChartTitle(chartModel);
    }
    const xAxisColumns = chartModelUtil.getXAxisColumns(chartModel);
    const yAxisColumns = chartModelUtil.getYAxisColumns(chartModel);
    const radialColumn = chartModelUtil.getRadialColumn(chartModel);
    const legendColumns = chartModelUtil.getLegendColumns(chartModel);

    let title = (yAxisColumns ?? [])
        .map(column => {
            return getName(column);
        })
        .join(', ');
    if (radialColumn) {
        title += ' ';
        title += translate('AND');
        title += ' ';
        title += getName(radialColumn);
    }
    if (xAxisColumns && xAxisColumns.length) {
        title += ' ';
        title += translate('BY');
        title += ' ';
        title += xAxisColumns
            .map(column => {
                return getName(column);
            })
            .join(', ');
    }
    if (legendColumns && legendColumns.length) {
        title += ' ';
        title += translate('AND');
        title += ' ';
        title += legendColumns
            .map(column => {
                return getName(column);
            })
            .join(', ');
    }
    return title;
};

/**
 * Get table title
 * @return { string } title of table
 *
 */
// TODO Use Radial columns and Legends to autogenerate Title
export const getTableAutoTitle = (tableViz: TableViz) => {
    // An answer whose sage query was cleared has a viz with no columns; this
    // runs inside Apollo cache reads, so throwing here crashes the whole
    // conversation error boundary.
    const columns = tableViz?.columns ?? [];
    const groupingColumns: Array<AnswerColumn> = [];
    const valueColumns: Array<AnswerColumn> = [];
    columns.forEach(column => {
        if (isGroupingColumn(column.column)) {
            groupingColumns.push(column.column);
        } else {
            valueColumns.push(column.column);
        }
    });

    const valueColumnNames = valueColumns
        .map(column => getName(column))
        .join(', ');
    const groupingColumnNames = groupingColumns
        .map(column => getName(column))
        .join(', ');
    let title = '';
    if (valueColumnNames && groupingColumnNames) {
        title = `${valueColumnNames} ${translate('BY')} ${groupingColumnNames}`;
    } else if (valueColumnNames) {
        title = valueColumnNames;
    } else if (groupingColumnNames) {
        title = groupingColumnNames;
    }
    return title;
};

/**
 * Get Autotitle
 * @return { string } title based on display mode ( table or chart)
 *
 */
export function autoTitleAnswer(
    name: string,
    displayMode: DisplayMode,
    vizs: Array<ChartViz | HeadlineViz | TableViz>,
) {
    let title = (name || '').trim();
    /* If title already exist then skip further steps and return the existing title  */
    // TODO remove condition for title value 'Untitled'  as it can be valid title
    // This hack is also being used in auto answers. When we deprecate/fix this
    // we should also look into fixing it in AnswerEdocGeneratorImpl.java
    if (title && title !== 'Untitled') {
        return title;
    }

    if (!vizs?.length) {
        return title;
    }

    if (displayMode === DisplayMode.TableMode) {
        // eslint-disable-next-line no-underscore-dangle
        const viz = vizs.find(v => v.__typename === 'TableViz');
        title = !_.isNil(viz) ? getTableAutoTitle(viz as TableViz) : title;
    } else {
        // eslint-disable-next-line no-underscore-dangle
        const viz = vizs.find(v => v.__typename === 'ChartViz');
        title = !_.isNil(viz) ? getChartAutoTitle(viz as ChartViz) : title;
    }
    return title;
}

export function getTextFromHtml(html: string, fallBack = false) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || (fallBack ? html : '');
}
